import { SearchResultItem } from "@/components/layout-2/types2";
import { DataService } from "@/services/data";
import { AuthService } from "@/services/auth";

export const SEARCH_LIGHT_CACHE_TTL_MS = 60_000;
export const SEARCH_RECENTS_KEY = "walter-crm:search-recents";
export const SEARCH_RECENTS_MAX = 8;
// ponytail: fixed-size chunks sent in parallel. Smaller chunks paint the first types sooner but hold more
// gunicorn threads; larger ones do the opposite. Upgrade: size the first chunk smaller than the rest.
export const SEARCH_MULTI_CHUNK_SIZE = 5;
export const SEARCH_TIMEOUT_MESSAGE = "Search took too long. Try a more specific query.";

export type SearchMultiBlock<T> = {
    icon?: string;
    color?: string;
    model_name?: string;
    model_name_plural?: string;
    columns?: string[];
    results: T[];
};

export type SearchMultiResponse<T = SearchResultItem> = {
    results: Record<string, SearchMultiBlock<T>>;
    errors: Record<string, string>;
};

export type SearchRecentItem = SearchResultItem & {
    icon?: string;
    color?: string;
};

const lightCache = new Map<string, { expiresAt: number; data: unknown }>();

function currentUserId(): string | null {
    try {
        const user = JSON.parse(localStorage.getItem("current_user") || "null");
        return user?.id == null ? null : String(user.id);
    } catch {
        return null;
    }
}

function recentsKey(): string | null {
    const userId = currentUserId();
    return userId ? `${SEARCH_RECENTS_KEY}:${userId}` : null;
}

function cacheKey(query: string, type: string): string {
    return `${AuthService.get_tokens()?.access ?? "anonymous"}:${type}:${query.trim().toLowerCase()}`;
}

export function resetSearchLightCache(): void {
    lightCache.clear();
}

export async function fetchSearchLight(query: string, type: string, controller?: AbortController): Promise<any> {
    const key = cacheKey(query, type);
    const hit = lightCache.get(key);
    if (hit && hit.expiresAt > Date.now()) {
        return hit.data;
    }
    const client = controller ? DataService.client.signal(controller) : DataService.client;
    const data = await client.url("/crm/app/search/light/").query({ q: query, type }).get().json<any>();
    lightCache.set(key, { expiresAt: Date.now() + SEARCH_LIGHT_CACHE_TTL_MS, data });
    return data;
}

export function chunkSearchTypes<T>(items: T[], size = SEARCH_MULTI_CHUNK_SIZE): T[][] {
    return Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size));
}

export async function fetchSearchMulti<T = SearchResultItem>(
    query: string,
    types: string[],
    mode: "light" | "global",
    controller?: AbortController
): Promise<SearchMultiResponse<T>> {
    const client = controller ? DataService.client.signal(controller) : DataService.client;
    return client.url("/crm/app/search/multi/").query({ q: query, types: types.join(","), mode }).get().json<SearchMultiResponse<T>>();
}

/** Light search for several types, reusing and filling the per-type cache `fetchSearchLight` reads. */
export async function fetchSearchLightMulti(query: string, types: string[], controller?: AbortController): Promise<SearchMultiResponse> {
    const results: SearchMultiResponse["results"] = {};
    const missing: string[] = [];
    for (const type of types) {
        const hit = lightCache.get(cacheKey(query, type));
        if (hit && hit.expiresAt > Date.now()) {
            Object.assign(results, hit.data as SearchMultiResponse["results"]);
        } else {
            missing.push(type);
        }
    }
    if (missing.length === 0) {
        return { results, errors: {} };
    }
    const data = await fetchSearchMulti(query, missing, "light", controller);
    const expiresAt = Date.now() + SEARCH_LIGHT_CACHE_TTL_MS;
    for (const type of missing) {
        if (!(type in data.errors)) {
            lightCache.set(cacheKey(query, type), { expiresAt, data: data.results[type] ? { [type]: data.results[type] } : {} });
        }
    }
    return { results: { ...results, ...data.results }, errors: data.errors };
}

export function loadSearchRecents(): SearchRecentItem[] {
    try {
        const key = recentsKey();
        if (!key) {
            return [];
        }
        const raw = localStorage.getItem(key);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter(item => item && typeof item.href === "string" && typeof item.title === "string");
    } catch {
        return [];
    }
}

export function rememberSearchClick(item: SearchRecentItem): SearchRecentItem[] {
    const key = recentsKey();
    if (!key) {
        return [];
    }
    const next = [item, ...loadSearchRecents().filter(existing => existing.href !== item.href)].slice(0, SEARCH_RECENTS_MAX);
    localStorage.setItem(key, JSON.stringify(next));
    return next;
}

export function filterSearchRecents(items: SearchRecentItem[], query: string): SearchRecentItem[] {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return items;
    }
    return items.filter(item => {
        const haystack = `${item.title} ${item.subtitle ?? ""}`.toLowerCase();
        return haystack.includes(needle);
    });
}
